import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KanbanServiceV2 } from '../services/kanbanServiceV2';
import { KanbanCard, KanbanBoard as KanbanBoardType } from '../types/kanban';
import {
  Card,
  Body1,
  Caption1,
  Title3,
  Badge,
  Spinner,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { CalendarRegular, PersonRegular, TagRegular, DocumentRegular, ChatRegular, OpenRegular } from '@fluentui/react-icons';
import { app, pages } from '@microsoft/teams-js';
import config from './sample/lib/config';

const kanbanService = KanbanServiceV2.getInstance();

const useStyles = makeStyles({
  container: {
    padding: '48px 32px',
    maxWidth: '680px',
    margin: '0 auto',
    backgroundColor: tokens.colorNeutralBackground1,
    minHeight: '100vh',
    overflowY: 'auto',
    height: '100vh',
    boxSizing: 'border-box',
  },
  card: {
    marginTop: '4px',
    marginBottom: '16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: 'none',
  },
  header: {
    marginBottom: '40px',
  },
  title: {
    marginBottom: '16px',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    marginBottom: '12px',
    color: tokens.colorNeutralForeground1,
    fontWeight: 600,
    fontSize: '14px',
  },
  priorityBadge: {
    fontSize: '12px',
    fontWeight: 500,
    padding: '4px 10px',
  },
  metadata: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px',
    padding: '20px',
  },
  metadataItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
  },
  description: {
    whiteSpace: 'pre-wrap',
    lineHeight: '1.7',
    padding: '20px',
    color: tokens.colorNeutralForeground1,
  },
  assignees: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '4px',
  },
  assigneeBadge: {
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 500,
  },
  commentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '4px',
  },
  commentItem: {
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: `3px solid ${tokens.colorBrandBackground}`,
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    marginBottom: '10px',
  },
  commentAuthor: {
    fontWeight: 600,
    fontSize: '14px',
    color: tokens.colorBrandForeground1,
  },
  commentDate: {
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
  },
  commentText: {
    display: 'block',
    lineHeight: '1.6',
    color: tokens.colorNeutralForeground1,
    fontSize: '13px',
  },
  attachmentCard: {
    marginBottom: '10px',
    padding: '16px 20px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: 'none',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  error: {
    padding: '48px 24px',
    textAlign: 'center',
    color: tokens.colorPaletteRedForeground1,
  },
  // Highlight styles
  highlighted: {
    border: `2px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  highlightedCard: {
    border: `2px solid ${tokens.colorBrandBackground}`,
  },
  highlightedComment: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeft: `3px solid ${tokens.colorBrandBackground}`,
  },
  highlightBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
    fontSize: '13px',
    fontWeight: 500,
  },
  goToTaskButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 20px',
    marginBottom: '24px',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: tokens.borderRadiusMedium,
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
});

function getHighlightInfo(actionType: string | null): { section: string; label: string } | null {
  if (!actionType) return null;
  if (actionType.includes('commented')) return { section: 'comments', label: 'New comment added' };
  if (actionType.includes('attachment')) return { section: 'attachments', label: 'New attachment added' };
  if (actionType.includes('assigned')) return { section: 'assignees', label: 'You were assigned to this task' };
  if (actionType.includes('moved')) return { section: 'metadata', label: 'Task was moved' };
  if (actionType.includes('updated')) return { section: 'description', label: 'Task details were updated' };
  return null;
}

export default function NotificationDetail() {
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const [card, setCard] = useState<KanbanCard | null>(null);
  const [board, setBoard] = useState<KanbanBoardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cardId = searchParams.get('cardId');
  const boardId = searchParams.get('boardId');
  const teamId = searchParams.get('teamId');
  const channelId = searchParams.get('channelId');
  const actionType = searchParams.get('actionType');
  const highlightInfo = getHighlightInfo(actionType);

  const goToTask = async () => {
    if (!teamId || !channelId) {
      console.error('Missing teamId or channelId:', { teamId, channelId });
      return;
    }

    try {
      await app.initialize();
    } catch (e) {
      // Already initialized, that's fine
    }

    // Navigate to the channel first, then the tab will be there
    const channelLink = `https://teams.microsoft.com/l/channel/${channelId}/?groupId=${teamId}`;

    // Use pages.navigateToApp to open our app's tab in the channel context
    try {
      await pages.navigateToApp({
        appId: config.clientId!,
        pageId: 'kanban-board',
        subPageId: cardId || undefined,
        channelId: channelId,
      });
      console.log('navigateToApp succeeded');
      return;
    } catch (e) {
      console.error('navigateToApp failed:', e);
    }

    // Fallback: at least get to the channel
    try {
      await app.openLink(channelLink);
    } catch (e) {
      console.error('openLink failed:', e);
      window.location.href = channelLink;
    }
  };

  useEffect(() => {
    const loadCardDetails = async () => {
      if (!cardId || !boardId) {
        setError('Missing task or board information');
        setLoading(false);
        return;
      }

      try {
        // Fetch board data
        const boardData = await kanbanService.getBoard(boardId);
        if (!boardData) {
          setError('Board not found');
          setLoading(false);
          return;
        }

        // Find the card
        const cardData = boardData.cards.find((c: KanbanCard) => c.id === cardId);
        if (!cardData) {
          setError('Task not found');
          setLoading(false);
          return;
        }

        setBoard(boardData);
        setCard(cardData);
      } catch (err) {
        console.error('Error loading card:', err);
        setError('Failed to load task details');
      } finally {
        setLoading(false);
      }
    };

    loadCardDetails();
  }, [cardId, boardId]);

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'informative';
    }
  };

  const getColumnName = () => {
    if (!board || !card) return 'Unknown';
    const column = board.columns.find(col => col.id === card.columnId);
    return column?.title || 'Unknown';
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <Spinner size="large" label="Loading task details..." />
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className={classes.error}>
        <Title3>{error || 'Task not found'}</Title3>
      </div>
    );
  }

  const isHighlighted = (section: string) => highlightInfo?.section === section;

  return (
    <div className={classes.container}>
      <button className={classes.goToTaskButton} onClick={goToTask}>
        <OpenRegular />
        Go to task
      </button>

      {highlightInfo && (
        <div className={classes.highlightBanner}>
          {highlightInfo.label}
        </div>
      )}

      <div className={classes.header}>
        <div className={classes.title}>
          <Title3>{card.title}</Title3>
        </div>
        {card.priority && (
          <Badge appearance="filled" color={getPriorityColor(card.priority)} className={classes.priorityBadge}>
            {card.priority.toUpperCase()}
          </Badge>
        )}
      </div>

      <Card className={mergeClasses(classes.card, isHighlighted('metadata') && classes.highlightedCard)}>
        <div className={classes.metadata}>
          <div className={classes.metadataItem}>
            <TagRegular />
            <Caption1>Column: {getColumnName()}</Caption1>
          </div>
          {card.dueDate && (
            <div className={classes.metadataItem}>
              <CalendarRegular />
              <Caption1>Due: {new Date(card.dueDate).toLocaleDateString()}</Caption1>
            </div>
          )}
          {card.assignedTo && card.assignedTo.length > 0 && (
            <div className={classes.metadataItem}>
              <PersonRegular />
              <Caption1>{card.assignedTo.length} assigned</Caption1>
            </div>
          )}
          {card.comments && card.comments.length > 0 && (
            <div className={classes.metadataItem}>
              <ChatRegular />
              <Caption1>{card.comments.length} comments</Caption1>
            </div>
          )}
          {card.attachments && card.attachments.length > 0 && (
            <div className={classes.metadataItem}>
              <DocumentRegular />
              <Caption1>{card.attachments.length} attachments</Caption1>
            </div>
          )}
        </div>
      </Card>

      {card.description && (
        <div className={classes.section}>
          <Caption1 className={classes.sectionTitle}>Description</Caption1>
          <Card className={mergeClasses(classes.card, isHighlighted('description') && classes.highlightedCard)}>
            <Body1 className={classes.description}>{card.description}</Body1>
          </Card>
        </div>
      )}

      {card.assignedTo && card.assignedTo.length > 0 && (
        <div className={mergeClasses(classes.section, isHighlighted('assignees') && classes.highlighted)} style={isHighlighted('assignees') ? { padding: '16px' } : undefined}>
          <Caption1 className={classes.sectionTitle}>Assigned To</Caption1>
          <div className={classes.assignees}>
            {card.assignedTo.map((user) => (
              <Badge key={user.id} className={classes.assigneeBadge}>
                {user.displayName}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {card.comments && card.comments.length > 0 && (
        <div className={classes.section}>
          <Caption1 className={classes.sectionTitle}>Recent Comments ({card.comments.length})</Caption1>
          <div className={classes.commentsContainer}>
            {card.comments.slice(-3).reverse().map((comment, index) => (
              <div
                key={comment.id}
                className={mergeClasses(
                  classes.commentItem,
                  isHighlighted('comments') && index === 0 && classes.highlightedComment
                )}
              >
                <div className={classes.commentHeader}>
                  <span className={classes.commentAuthor}>{comment.author.displayName}</span>
                  <span className={classes.commentDate}>• {new Date(comment.createdAt).toLocaleString()}</span>
                </div>
                <Body1 className={classes.commentText}>{comment.text}</Body1>
              </div>
            ))}
          </div>
        </div>
      )}

      {card.attachments && card.attachments.length > 0 && (
        <div className={classes.section}>
          <Caption1 className={classes.sectionTitle}>Attachments ({card.attachments.length})</Caption1>
          {card.attachments.map((attachment, index) => (
            <Card
              key={attachment.id}
              className={mergeClasses(
                classes.card,
                isHighlighted('attachments') && index === card.attachments!.length - 1 && classes.highlightedCard
              )}
            >
              <div style={{ padding: '16px 20px' }}>
                <div className={classes.metadataItem}>
                  <DocumentRegular />
                  <Body1 style={{ fontWeight: 500 }}>{attachment.name}</Body1>
                </div>
                <div style={{ marginTop: '8px', display: 'flex', gap: '16px' }}>
                  {attachment.uploadedBy && (
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      Uploaded by {typeof attachment.uploadedBy === 'object' ? attachment.uploadedBy.displayName : 'Unknown'}
                    </Caption1>
                  )}
                  {attachment.uploadedAt && (
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                      {new Date(attachment.uploadedAt).toLocaleString()}
                    </Caption1>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
